import { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import {
  X,
  Upload,
  Image,
  FileSpreadsheet,
  AlertTriangle,
  Table,
  Plus,
  Trash2,
  ChevronDown,
  Type,
  Hash,
  Calendar,
  ToggleLeft,
  Loader2,
  File,
  GripVertical,
  FileText,
  FileImage,
  FileVideo,
  FileAudio,
  Archive,
  Code,
} from 'lucide-react'
import { useTranslations } from 'next-intl'
import { parse as csvParse } from 'papaparse'
import * as XLSX from 'xlsx'

interface ColumnDefinition {
  name: string
  type:
    | 'id'
    | 'number'
    | 'timestamp'
    | 'tag'
    | 'media_url'
    | 'file_url'
    | 'text'
    | 'rich_text'
    | 'checkbox'
    | 'web_url'
    | 'address'
  width: number
}

interface TableData {
  headers: string[]
  rows: string[][]
  columns?: ColumnDefinition[]
}

interface TableUploadModalProps {
  onSave: (
    config: { ui_table_name: string; db_table_name: string; field_list: any[] },
    rows: string[][],
    mongoId?: string,
  ) => void
  onClose: () => void
  initialData?: TableData
  loadTableId?: string // Optional table ID to load from MongoDB
  csvIndex?: number // Optional CSV index when loading from a multi-CSV entry
}

export function TableUploadModal({
  onSave,
  onClose,
  initialData,
  loadTableId,
  csvIndex,
}: TableUploadModalProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDetectingTypes, setIsDetectingTypes] = useState(false)
  const [showTypeDetectionMessage, setShowTypeDetectionMessage] =
    useState(false)
  const [tableData, setTableData] = useState<TableData | null>(
    initialData || null,
  )
  const [tableName, setTableName] = useState('Data Table')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [resizingColumn, setResizingColumn] = useState<number | null>(null)
  const [resizeStartX, setResizeStartX] = useState<number>(0)
  const [resizeStartWidth, setResizeStartWidth] = useState<number>(0)
  const [showRowsDropdown, setShowRowsDropdown] = useState(false)
  const [visibleRows, setVisibleRows] = useState(5)
  const [columnDropdowns, setColumnDropdowns] = useState<{
    [key: number]: boolean
  }>({})
  const [addColumnDropdown, setAddColumnDropdown] = useState(false)
  const [addColumnButtonPosition, setAddColumnButtonPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [uploadingCells, setUploadingCells] = useState<{
    [key: string]: boolean
  }>({})
  const [uploadErrors, setUploadErrors] = useState<{ [key: string]: string }>(
    {},
  )
  const [draggingColumn, setDraggingColumn] = useState<number | null>(null)
  const [dragOverColumn, setDragOverColumn] = useState<number | null>(null)
  const [imagePreview, setImagePreview] = useState<{
    url: string
    alt: string
  } | null>(null)
  const [editingTagCells, setEditingTagCells] = useState<{
    [key: string]: boolean
  }>({})
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const tableRef = useRef<HTMLDivElement>(null)
  const addColumnButtonRef = useRef<HTMLButtonElement>(null)
  const cellFileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>(
    {},
  )
  const t = useTranslations('modals.table-upload')

  // File type detection using asset type approach (like Hero.tsx)
  type IconComponent = typeof File
  const categoryIconMap: Record<string, IconComponent> = {
    video: FileVideo,
    audio: FileAudio,
    image: FileImage,
    text: FileText,
    archive: Archive,
    code: Code,
  }

  const getFileIcon = (url: string) => {
    if (!url) return File

    const getAssetType = (url: string): string | undefined => {
      const extension = url.split('.').pop()?.toLowerCase().split('?')[0]
      if (!extension) return undefined
      if (
        ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)
      )
        return 'image'
      if (
        ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)
      )
        return 'video'
      if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma'].includes(extension))
        return 'audio'
      if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension))
        return 'archive'
      if (
        [
          'js',
          'ts',
          'html',
          'css',
          'json',
          'xml',
          'py',
          'java',
          'cpp',
          'c',
        ].includes(extension)
      )
        return 'code'
      if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(extension))
        return 'text'
      return undefined
    }

    const assetType = getAssetType(url)

    if (assetType && categoryIconMap[assetType]) {
      return categoryIconMap[assetType]
    }

    return File
  }

  const isImageUrl = (url: string): boolean => {
    const extension = url.split('.').pop()?.toLowerCase().split('?')[0]
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(
      extension || '',
    )
  }

  const isUrl = (str: string): boolean => {
    try {
      new URL(str)
      return true
    } catch {
      return false
    }
  }

  const getFileName = (url: string): string => {
    try {
      const urlObj = new URL(url)
      return urlObj.pathname.split('/').pop() || 'file'
    } catch {
      return url.split('/').pop() || 'file'
    }
  }

  // Check if a row contains any images for height adjustment
  const rowHasImages = useCallback(
    (row: string[], columns: ColumnDefinition[]): boolean => {
      return row.some((cellValue, index) => {
        const column = columns[index]
        return (
          column?.type === 'media_url' && cellValue && isImageUrl(cellValue)
        )
      })
    },
    [],
  )

  const columnTypes = [
    { value: 'text', label: t('column_type_text'), icon: Type },
    { value: 'number', label: t('column_type_number'), icon: Hash },
    { value: 'timestamp', label: t('column_type_timestamp'), icon: Calendar },
    { value: 'checkbox', label: t('column_type_checkbox'), icon: ToggleLeft },
    { value: 'tag', label: t('column_type_tag'), icon: Hash },
    { value: 'media_url', label: t('column_type_media_url'), icon: Upload },
    {
      value: 'file_url',
      label: t('column_type_file_url'),
      icon: FileSpreadsheet,
    },
    { value: 'rich_text', label: t('column_type_rich_text'), icon: Type },
    { value: 'web_url', label: t('column_type_web_url'), icon: Upload },
    { value: 'address', label: t('column_type_address'), icon: Type },
  ]

  const placeholderMap = useMemo(
    () => ({
      text: t('enter_text'),
      number: t('enter_number'),
      timestamp: t('enter_timestamp'),
      checkbox: t('enter_checkbox'),
      tag: t('enter_tag'),
      media_url: t('enter_media_url'),
      file_url: t('enter_file_url'),
      rich_text: t('enter_rich_text'),
      web_url: t('enter_web_url'),
      address: t('enter_address'),
    }),
    [t],
  )

  const getPlaceholderForType = (type: string): string => {
    return (
      placeholderMap[type as keyof typeof placeholderMap] || placeholderMap.text
    )
  }

  // Simple column type detection - all logic is handled by the API route
  const detectColumnTypes = async (
    headers: string[],
    rows: string[][],
  ): Promise<string[]> => {
    setIsDetectingTypes(true)
    try {
      const sampleData = rows.slice(0, 10)

      const response = await fetch('/api/table/detect-types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headers, rows: sampleData }),
      })

      if (response.ok) {
        const data = await response.json()

        if (data.types && Array.isArray(data.types)) {
          // Show the type detection message after successful detection
          setShowTypeDetectionMessage(true)

          return data.types.map((type: string, index: number) => {
            const header = headers[index]
            return type
          })
        }
      }
    } catch (error) {
      // Handle detection error silently
    } finally {
      setIsDetectingTypes(false)
    }

    // Fallback: return 'text' for all columns
    const fallbackTypes = headers.map(() => 'text')

    return fallbackTypes
  }

  // Generate table configuration JSON
  const generateTableConfig = useCallback(() => {
    if (!tableData?.columns) return null

    const config = {
      ui_table_name: tableName.trim() || 'Data Table', // Ensure table name is valid
      db_table_name: '', // Generate db name from UI name
      field_list: tableData.columns.map((column, index) => {
        const hasEmptyValues = tableData.rows.some(
          (row) =>
            !row[index] ||
            (typeof row[index] === 'string' && row[index].trim() === '') ||
            row[index] === null ||
            row[index] === undefined,
        )
        return {
          db_name: column.name.toLowerCase().replace(/\s+/g, '_'),
          ui_name: column.name,
          type: column.type,
          required: column.type === 'id' && !hasEmptyValues, // ID columns are required only if no empty values
          foreign_key: {},
        }
      }),
    }

    return config
  }, [tableData, tableName])

  // Initialize columns when table data changes
  useEffect(() => {
    if (tableData && !tableData.columns) {
      const initializeColumns = async () => {
        // Detect column types using API
        const detectedTypes = await detectColumnTypes(
          tableData.headers,
          tableData.rows,
        )

        const columns: ColumnDefinition[] = tableData.headers.map(
          (header, index) => ({
            name: header || `Column ${index + 1}`,
            type: (detectedTypes[index] as any) || 'text',
            width: 150,
          }),
        )

        setTableData({ ...tableData, columns })
      }

      initializeColumns()
    }
  }, [tableData, detectColumnTypes])

  // Log table configuration and data structure after upload completion
  useEffect(() => {
    if (tableData && tableData.columns) {
      const config = generateTableConfig()
    }
  }, [tableData, generateTableConfig])

  const addColumn = useCallback(
    (type: string) => {
      if (!tableData) return
      const newColumnIndex = tableData.headers.length
      const newColumn: ColumnDefinition = {
        name: `Column ${newColumnIndex + 1}`,
        type: type as any,
        width: 150,
      }
      const updatedTableData = {
        ...tableData,
        headers: [...tableData.headers, newColumn.name],
        columns: [...(tableData.columns || []), newColumn],
        rows: tableData.rows.map((row) => [...row, '']),
      }
      setTableData(updatedTableData)
      setAddColumnDropdown(false)
      setAddColumnButtonPosition(null)

      // Scroll to the new column after state update
      setTimeout(() => {
        if (tableRef.current) {
          tableRef.current.scrollLeft = tableRef.current.scrollWidth
        }
      }, 0)
    },
    [tableData],
  )

  const handleAddColumnClick = useCallback(() => {
    if (addColumnButtonRef.current) {
      const rect = addColumnButtonRef.current.getBoundingClientRect()
      setAddColumnButtonPosition({
        x: rect.right - 160, // Position dropdown to the left of the button
        y: rect.bottom + 4, // Position dropdown below the button
      })
      setAddColumnDropdown(true)
    }
  }, [])

  const deleteColumn = useCallback(
    (columnIndex: number) => {
      if (!tableData) return

      const updatedTableData = {
        ...tableData,
        headers: tableData.headers.filter((_, index) => index !== columnIndex),
        columns: tableData.columns?.filter((_, index) => index !== columnIndex),
        rows: tableData.rows.map((row) =>
          row.filter((_, index) => index !== columnIndex),
        ),
      }

      setTableData(updatedTableData)
    },
    [tableData],
  )

  const addRow = useCallback(() => {
    if (!tableData) return

    const newRow = new Array(tableData.headers.length).fill('')
    const updatedTableData = {
      ...tableData,
      rows: [...tableData.rows, newRow],
    }

    setTableData(updatedTableData)

    // Show the new row immediately if it's beyond visible rows
    if (visibleRows < updatedTableData.rows.length) {
      setVisibleRows(updatedTableData.rows.length)
      setTimeout(() => {
        if (tableRef.current) {
          tableRef.current.scrollTop = tableRef.current.scrollHeight
        }
      }, 0)
    }
  }, [tableData, visibleRows])

  const deleteRow = useCallback(
    (rowIndex: number) => {
      if (!tableData) return

      const updatedTableData = {
        ...tableData,
        rows: tableData.rows.filter((_, index) => index !== rowIndex),
      }

      setTableData(updatedTableData)
    },
    [tableData],
  )

  const updateColumnName = useCallback(
    (columnIndex: number, newName: string) => {
      if (!tableData) return

      const updatedTableData = {
        ...tableData,
        headers: tableData.headers.map((header, index) =>
          index === columnIndex ? newName : header,
        ),
        columns: tableData.columns?.map((column, index) =>
          index === columnIndex ? { ...column, name: newName } : column,
        ),
      }

      setTableData(updatedTableData)
    },
    [tableData],
  )

  const updateColumnType = useCallback(
    (columnIndex: number, newType: string) => {
      if (!tableData) return

      const updatedTableData = {
        ...tableData,
        columns: tableData.columns?.map((column, index) =>
          index === columnIndex ? { ...column, type: newType as any } : column,
        ),
      }

      setTableData(updatedTableData)
    },
    [tableData],
  )

  // File upload functionality for media_url and file_url columns
  const handleCellFileUpload = useCallback(
    async (file: File, rowIndex: number, colIndex: number) => {
      const cellKey = `${rowIndex}-${colIndex}`
      setUploadingCells((prev) => ({ ...prev, [cellKey]: true }))
      setUploadErrors((prev) => ({ ...prev, [cellKey]: '' }))

      try {
        // Convert file to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader()
          reader.onloadend = () => {
            const result = reader.result as string
            const base64Data = result.split(',')[1] // Remove data URL prefix
            resolve(base64Data)
          }
          reader.onerror = reject
          reader.readAsDataURL(file)
        })

        // Upload to OSS
        const response = await fetch('/api/table/upload-file', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ base64_data: base64 }),
        })

        if (!response.ok) {
          throw new Error('Upload failed: Server returned an error')
        }

        const data = await response.json()

        if (!data.url) {
          throw new Error('Upload failed: No URL returned')
        }

        // Update cell with the uploaded file URL
        if (!tableData) return

        const updatedTableData = {
          ...tableData,
          rows: tableData.rows.map((row, rIndex) =>
            rIndex === rowIndex
              ? row.map((cell, cIndex) =>
                  cIndex === colIndex ? data.url : cell,
                )
              : row,
          ),
        }

        setTableData(updatedTableData)
      } catch (error: any) {
        setUploadErrors((prev) => ({
          ...prev,
          [cellKey]: error.message || 'Upload failed',
        }))
      } finally {
        setUploadingCells((prev) => ({ ...prev, [cellKey]: false }))
      }
    },
    [tableData],
  )

  const triggerCellFileUpload = useCallback(
    (rowIndex: number, colIndex: number) => {
      const cellKey = `${rowIndex}-${colIndex}`
      const input = cellFileInputRefs.current[cellKey]
      if (input) {
        input.click()
      }
    },
    [],
  )

  // Column drag and drop functionality
  const handleColumnDragStart = useCallback(
    (e: React.DragEvent, columnIndex: number) => {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', columnIndex.toString())
      setDraggingColumn(columnIndex)
    },
    [],
  )

  const handleColumnDragOver = useCallback(
    (e: React.DragEvent, columnIndex: number) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDragOverColumn(columnIndex)
    },
    [],
  )

  const handleColumnDragLeave = useCallback(() => {
    setDragOverColumn(null)
  }, [])

  const handleColumnDrop = useCallback(
    (e: React.DragEvent, targetIndex: number) => {
      e.preventDefault()

      if (draggingColumn === null || !tableData?.columns) return

      if (draggingColumn === targetIndex) {
        setDraggingColumn(null)
        setDragOverColumn(null)
        return
      }

      // Reorder columns
      const newColumns = [...tableData.columns]
      const newHeaders = [...tableData.headers]
      const newRows = tableData.rows.map((row) => [...row])

      // Remove the dragged column
      const [draggedColumn] = newColumns.splice(draggingColumn, 1)
      const [draggedHeader] = newHeaders.splice(draggingColumn, 1)

      // Insert at target position
      newColumns.splice(targetIndex, 0, draggedColumn)
      newHeaders.splice(targetIndex, 0, draggedHeader)

      // Reorder data in rows
      newRows.forEach((row) => {
        const [draggedCell] = row.splice(draggingColumn, 1)
        row.splice(targetIndex, 0, draggedCell)
      })

      setTableData({
        ...tableData,
        headers: newHeaders,
        columns: newColumns,
        rows: newRows,
      })

      setDraggingColumn(null)
      setDragOverColumn(null)
    },
    [draggingColumn, tableData],
  )

  const handleColumnDragEnd = useCallback(() => {
    setDraggingColumn(null)
    setDragOverColumn(null)
  }, [])

  const updateCellValue = useCallback(
    (rowIndex: number, columnIndex: number, value: string) => {
      if (!tableData) return

      const updatedTableData = {
        ...tableData,
        rows: tableData.rows.map((row, rIndex) =>
          rIndex === rowIndex
            ? row.map((cell, cIndex) => (cIndex === columnIndex ? value : cell))
            : row,
        ),
      }

      setTableData(updatedTableData)
    },
    [tableData],
  )

  // Render cell content based on column type
  const renderCellContent = useCallback(
    (
      column: ColumnDefinition,
      value: string,
      rowIndex: number,
      colIndex: number,
    ) => {
      if (column.type === 'checkbox') {
        return (
          <div className="flex items-center justify-center px-3 py-2">
            <input
              type="checkbox"
              checked={
                value === 'true' ||
                value === '1' ||
                value.toLowerCase() === 'yes'
              }
              onChange={(e) =>
                updateCellValue(
                  rowIndex,
                  colIndex,
                  e.target.checked ? 'true' : 'false',
                )
              }
              className="w-4 h-4 bg-transparent border border-white/30 rounded text-blue-500 focus:ring-blue-500 focus:ring-2"
            />
          </div>
        )
      }

      if (column.type === 'tag') {
        const tags = value
          ? value
              .split(',')
              .map((tag) => tag.trim())
              .filter((tag) => tag.length > 0)
          : []
        const cellKey = `${rowIndex}-${colIndex}`
        const isEditing = editingTagCells[cellKey] || false

        return (
          <div className="px-3 py-2 relative">
            {isEditing ? (
              <input
                type="text"
                value={value || ''}
                onChange={(e) =>
                  updateCellValue(rowIndex, colIndex, e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setEditingTagCells((prev) => ({
                      ...prev,
                      [cellKey]: false,
                    }))
                  }
                }}
                onBlur={() =>
                  setEditingTagCells((prev) => ({ ...prev, [cellKey]: false }))
                }
                autoFocus
                className="w-full bg-transparent text-white opacity-90 text-sm border-none outline-none focus:bg-white/5"
                placeholder={t('enter_tags')}
              />
            ) : (
              <div
                className="flex flex-wrap gap-1 cursor-text min-h-[1.5rem] items-center"
                onClick={() =>
                  setEditingTagCells((prev) => ({ ...prev, [cellKey]: true }))
                }
              >
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-300 border border-blue-500/30"
                  >
                    {tag}
                  </span>
                ))}
                {tags.length === 0 && (
                  <span className="text-white opacity-40 text-sm">
                    Click to add tags
                  </span>
                )}
              </div>
            )}
          </div>
        )
      }

      // File upload functionality for media_url and file_url columns
      if (column.type === 'media_url' || column.type === 'file_url') {
        const cellKey = `${rowIndex}-${colIndex}`
        const isUploading = uploadingCells[cellKey]
        const uploadError = uploadErrors[cellKey]
        const acceptTypes =
          column.type === 'media_url'
            ? 'image/*,video/*,audio/*'
            : '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar'

        return (
          <div className="px-3 py-2 group relative">
            <div className="flex items-center gap-2">
              {/* Left Icon */}
              <div className="flex-shrink-0 flex items-center justify-center">
                {value && isImageUrl(value) ? (
                  <img
                    src={value}
                    alt="Preview"
                    className="w-12 h-8 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() =>
                      setImagePreview({ url: value, alt: getFileName(value) })
                    }
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                ) : value && isUrl(value) ? (
                  (() => {
                    const IconComponent = getFileIcon(value)
                    return (
                      <IconComponent className="w-4 h-4 text-white opacity-60" />
                    )
                  })()
                ) : column.type === 'media_url' ? (
                  <Image className="w-4 h-4 text-white opacity-40" />
                ) : (
                  <File className="w-4 h-4 text-white opacity-40" />
                )}
              </div>

              {/* Input Field with right padding for upload button */}
              <input
                type="text"
                value={value || ''}
                onChange={(e) =>
                  updateCellValue(rowIndex, colIndex, e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur()
                  }
                }}
                className="flex-1 bg-transparent text-white opacity-90 text-sm border-none outline-none focus:bg-white/5 pr-8"
                placeholder={
                  column.type === 'media_url'
                    ? t('enter_media_url')
                    : t('enter_file_url')
                }
              />
            </div>

            {/* Upload Button - positioned absolutely over the right edge of the input */}
            <button
              onClick={() => triggerCellFileUpload(rowIndex, colIndex)}
              disabled={isUploading}
              className={`absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-[#404040] hover:bg-[#505050] disabled:bg-[#303030] rounded flex items-center justify-center transition-all shadow-sm ${
                value ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'
              }`}
              title={`Upload ${column.type === 'media_url' ? 'media' : 'file'}`}
            >
              {isUploading ? (
                <Loader2 className="w-3 h-3 text-white opacity-60 animate-spin" />
              ) : column.type === 'media_url' ? (
                <Upload className="w-3 h-3 text-white opacity-60" />
              ) : (
                <File className="w-3 h-3 text-white opacity-60" />
              )}
            </button>

            {uploadError && (
              <div className="text-red-400 text-xs mt-1">{uploadError}</div>
            )}

            <input
              ref={(el) => {
                cellFileInputRefs.current[cellKey] = el
              }}
              type="file"
              accept={acceptTypes}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  handleCellFileUpload(file, rowIndex, colIndex)
                }
              }}
            />
          </div>
        )
      }

      // Handle web_url column type
      if (column.type === 'web_url') {
        return (
          <div className="px-3 py-2">
            <input
              type="text"
              value={value || ''}
              onChange={(e) =>
                updateCellValue(rowIndex, colIndex, e.target.value)
              }
              className="w-full bg-transparent text-white opacity-90 text-sm border-none outline-none focus:bg-white/5"
              placeholder={t('enter_web_url')}
            />
          </div>
        )
      }

      // Default text input for all other types
      return (
        <div className="px-3 py-2">
          <input
            type="text"
            value={value || ''}
            onChange={(e) =>
              updateCellValue(rowIndex, colIndex, e.target.value)
            }
            className="w-full bg-transparent text-white opacity-90 text-sm border-none outline-none focus:bg-white/5"
            placeholder={getPlaceholderForType(column.type)}
          />
        </div>
      )
    },
    [
      updateCellValue,
      uploadingCells,
      uploadErrors,
      triggerCellFileUpload,
      handleCellFileUpload,
      editingTagCells,
      setEditingTagCells,
    ],
  )

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (!target.closest('.dropdown-container')) {
        setColumnDropdowns({})
        setAddColumnDropdown(false)
        setAddColumnButtonPosition(null)
        setShowRowsDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Cleanup drag state on unmount
  useEffect(() => {
    return () => {
      // Force clear all drag states
      setDraggingColumn(null)
      setDragOverColumn(null)
    }
  }, [])

  // Also clean up when modal closes or table data changes
  useEffect(() => {
    if (!tableData) {
      setDraggingColumn(null)
      setDragOverColumn(null)
    }
  }, [tableData])

  // Handle column resizing
  useEffect(() => {
    if (resizingColumn === null) return

    const handleMouseMove = (e: MouseEvent) => {
      if (!tableData?.columns) return

      const deltaX = e.clientX - resizeStartX
      const newWidth = Math.max(50, resizeStartWidth + deltaX) // minimum width of 50px

      const updatedTableData = {
        ...tableData,
        columns: tableData.columns.map((column, index) =>
          index === resizingColumn ? { ...column, width: newWidth } : column,
        ),
      }

      setTableData(updatedTableData)
    }

    const handleMouseUp = () => {
      setResizingColumn(null)
      setResizeStartX(0)
      setResizeStartWidth(0)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [resizingColumn, tableData, resizeStartX, resizeStartWidth])

  const handleResizeStart = useCallback(
    (columnIndex: number, e: React.MouseEvent) => {
      e.preventDefault()
      if (!tableData?.columns) return

      setResizingColumn(columnIndex)
      setResizeStartX(e.clientX)
      setResizeStartWidth(tableData.columns[columnIndex].width)
    },
    [tableData],
  )

  const parseCSVFile = useCallback(
    (file: File): Promise<TableData> => {
      return new Promise(async (resolve, reject) => {
        csvParse(file, {
          header: false,
          skipEmptyLines: true,
          complete: async (results) => {
            if (results.errors.length > 0) {
              reject(new Error('CSV parsing error'))
              return
            }

            const data = results.data as string[][]
            if (data.length === 0) {
              reject(new Error('Empty file'))
              return
            }

            const headers = data[0]
            const rows = data.slice(1)

            // Detect column types using API
            const detectedTypes = await detectColumnTypes(headers, rows)

            const columns: ColumnDefinition[] = headers.map(
              (header, index) => ({
                name: header || `Column ${index + 1}`,
                type: (detectedTypes[index] as any) || 'text',
                width: 150,
              }),
            )

            resolve({ headers, rows, columns })
          },
          error: (error) => {
            reject(error)
          },
        })
      })
    },
    [detectColumnTypes],
  )

  const parseXLSXFile = useCallback(
    (file: File): Promise<TableData> => {
      return new Promise(async (resolve, reject) => {
        const reader = new FileReader()
        reader.onload = async (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer)
            const workbook = XLSX.read(data, { type: 'array' })
            const sheetName = workbook.SheetNames[0]
            const worksheet = workbook.Sheets[sheetName]
            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
            }) as string[][]

            if (jsonData.length === 0) {
              reject(new Error('Empty file'))
              return
            }

            const headers = jsonData[0]
            const rows = jsonData.slice(1)

            // Detect column types using API
            const detectedTypes = await detectColumnTypes(headers, rows)

            const columns: ColumnDefinition[] = headers.map(
              (header, index) => ({
                name: header || `Column ${index + 1}`,
                type: (detectedTypes[index] as any) || 'text',
                width: 150,
              }),
            )

            resolve({ headers, rows, columns })
          } catch (error) {
            reject(new Error('Excel parsing error'))
          }
        }
        reader.onerror = () => reject(new Error('File reading error'))
        reader.readAsArrayBuffer(file)
      })
    },
    [detectColumnTypes],
  )

  const handleFileUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return

      const file = files[0]
      const fileExtension = file.name.split('.').pop()?.toLowerCase()

      if (!fileExtension || !['csv', 'xlsx'].includes(fileExtension)) {
        setErrorMessage('Please select a CSV or XLSX file')
        return
      }

      // Check file size limit (100KB)
      const maxSizeInBytes = 100 * 1024 // 100KB in bytes
      if (file.size > maxSizeInBytes) {
        setErrorMessage(t('file_size_limit_exceeded'))
        return
      }

      setIsUploading(true)
      setErrorMessage(null)

      try {
        let parsedData: TableData

        if (fileExtension === 'csv') {
          parsedData = await parseCSVFile(file)
        } else {
          parsedData = await parseXLSXFile(file)
        }

        // Set table name from file name (remove extension and clean up)
        const cleanFileName =
          file.name
            .replace(/\.(csv|xlsx)$/i, '') // Remove file extension
            .replace(/[^a-zA-Z0-9\s]/g, ' ') // Replace special characters with spaces
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .trim() // Remove leading/trailing spaces
            .split(' ') // Split into words
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            ) // Capitalize each word
            .join(' ') || 'Data Table' // Join with spaces, fallback to default

        setTableName(cleanFileName)
        setTableData(parsedData)
      } catch (error) {
        setErrorMessage(
          'Error parsing file. Please check the format and try again.',
        )
      } finally {
        setIsUploading(false)
      }
    },
    [parseCSVFile, parseXLSXFile],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      handleFileUpload(e.dataTransfer.files)
    },
    [handleFileUpload],
  )

  // Load table data from MongoDB
  const loadTableFromMongoDB = useCallback(
    async (tableId: string, csvIdx?: number) => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        const url = new URL('/api/table/save', window.location.origin)
        url.searchParams.set('user_table_entry_id', tableId)
        if (csvIdx !== undefined) {
          url.searchParams.set('csv_index', csvIdx.toString())
        }

        const response = await fetch(url.toString())

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || 'Failed to load table')
        }

        const data = await response.json()

        if (csvIdx !== undefined && data.table) {
          // Loading specific CSV from entry
          const { db_config, headers, rows } = data.table

          // Convert db_config to column definitions
          const columns: ColumnDefinition[] = Object.entries(
            db_config.columns,
          ).map(([name, config]: [string, any]) => ({
            name,
            type: config.type,
            width: 150, // Default width
          }))

          setTableData({ headers, rows, columns })
          setTableName(db_config.name || 'Loaded Table')
        } else if (data.entry) {
          // Loading full entry - use the first CSV by default
          const entry = data.entry
          if (entry.db_configs && entry.db_configs.length > 0) {
            const firstConfig = entry.db_configs[0]
            const firstHeaders = entry.header_arrays[0]
            const firstRows = entry.row_arrays[0]

            const columns: ColumnDefinition[] = Object.entries(
              firstConfig.columns,
            ).map(([name, config]: [string, any]) => ({
              name,
              type: config.type,
              width: 150,
            }))

            setTableData({ headers: firstHeaders, rows: firstRows, columns })
            setTableName(firstConfig.name || 'Loaded Table')
          }
        }
      } catch (error) {
        console.error('Error loading table:', error)
        setErrorMessage(
          error instanceof Error ? error.message : 'Failed to load table',
        )
      } finally {
        setIsLoading(false)
      }
    },
    [],
  )

  // Load table data when loadTableId is provided
  useEffect(() => {
    if (loadTableId) {
      loadTableFromMongoDB(loadTableId, csvIndex)
    }
  }, [loadTableId, csvIndex, loadTableFromMongoDB])

  const handleSaveTable = useCallback(async () => {
    if (!tableData) return

    // Show confirmation modal first
    setShowConfirmModal(true)
  }, [tableData])

  const handleConfirmSave = useCallback(async () => {
    if (!tableData) return

    setIsSaving(true)
    setErrorMessage(null)
    setShowConfirmModal(false)

    try {
      // Generate the table configuration
      const config = generateTableConfig()

      if (!config) {
        setErrorMessage('Failed to generate table configuration')
        return
      }

      console.log('=== SAVE TABLE DEBUG ===')
      console.log('Table config:', JSON.stringify(config, null, 2))

      console.log('Load table ID:', loadTableId)

      // Save to database
      const requestBody: any = {
        db_config: config,
        headers: tableData.headers,
        rows: tableData.rows,
      }

      // Add user_table_entry_id if updating existing entry
      if (loadTableId) {
        requestBody.user_table_entry_id = loadTableId
        console.log('Updating existing entry with ID:', loadTableId)
      } else {
        console.log('Creating new entry')
      }

      console.log('Request body keys:', Object.keys(requestBody))
      console.log('========================')

      const response = await fetch('/api/table/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        let errorMessage = 'Failed to save table'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorMessage
        } catch (jsonError) {
          // If response is not JSON (e.g., HTML error page), use status text
          errorMessage = `Server error (${response.status}): ${response.statusText}`
        }
        throw new Error(errorMessage)
      }

      let result
      try {
        result = await response.json()
      } catch (jsonError) {
        throw new Error('Invalid response format from server')
      }

      console.log('Table saved successfully:', result)

      // Call the original onSave callback with the config, rows, and MongoDB entry ID
      onSave(
        config, // Pass the config object
        tableData.rows, // Pass the actual row data for INSERT statements
        result.user_table_entry_id || result.entry_id || result.id,
      )
      onClose()
    } catch (error: any) {
      console.error('Error saving table:', error)
      setErrorMessage(
        error.message || 'Failed to save table. Please try again.',
      )
    } finally {
      setIsSaving(false)
    }
  }, [tableData, onSave, onClose, generateTableConfig])

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998]"
        onClick={onClose}
      />

      {/* Error Banner */}
      {errorMessage && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[99999] animate-in slide-in-from-top duration-300">
          <div className="bg-gradient-to-br from-[#222222]/95 via-[#2B2B2B] to-[#010101]/95 backdrop-blur-lg rounded-md px-4 py-3 shadow-lg border border-white/10 max-w-md mx-4 relative">
            <button
              onClick={() => setErrorMessage(null)}
              className="absolute top-2 right-2 text-white opacity-80 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 pr-6">
              <AlertTriangle className="w-5 h-5 text-white flex-shrink-0" />
              <div>
                <p className="text-white font-light text-sm">{errorMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-[99999] p-4">
        <div
          className={`bg-gradient-to-br from-[#222222]/95 via-[#2B2B2B] to-[#010101]/95 backdrop-blur-lg rounded-xl shadow-2xl border border-white/10 w-full ${
            tableData ? 'max-w-6xl' : 'max-w-2xl'
          } max-h-[95vh] overflow-hidden transition-all duration-500 ease-in-out`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-md flex items-center justify-center">
                <Table className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                {tableData ? (
                  <div className="flex flex-col">
                    <input
                      type="text"
                      value={tableName}
                      onChange={(e) => setTableName(e.target.value)}
                      className="text-xl font-semibold text-white bg-transparent border border-white/20 rounded px-2 py-1 text-left hover:border-white/40 focus:border-white/60 focus:outline-none transition-colors"
                      placeholder={t('enter_table_name')}
                    />
                    <p className="text-white opacity-60 text-sm text-left mt-1">
                      {t('customize_table_properties')}
                    </p>
                  </div>
                ) : (
                  <div>
                    <h2 className="text-xl font-semibold text-white text-left">
                      {t('title')}
                    </h2>
                    <p className="text-white opacity-60 text-sm text-left">
                      {t('description')}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {tableData && (
                <button
                  onClick={() => setTableData(null)}
                  className="text-white opacity-60 hover:text-white transition-colors text-sm px-3 py-1 hover:bg-white/10 rounded"
                >
                  {t('upload_different_file')}
                </button>
              )}
              <button
                onClick={onClose}
                className="text-white opacity-60 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-md"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="p-6 max-h-[calc(95vh-140px)] flex flex-col">
            {isLoading ? (
              /* Loading from MongoDB */
              <div className="flex-1 flex items-center justify-center">
                <div className="bg-[#222222] border border-white/10 rounded-lg p-6 flex items-center gap-3">
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white animate-spin rounded-full" />
                  <span className="text-white text-sm">
                    Loading table data...
                  </span>
                </div>
              </div>
            ) : !tableData ? (
              /* Upload Area */
              <div
                className={`border-2 border-dashed rounded-md p-8 text-center transition-all duration-200 ${
                  isDragging
                    ? 'border-white/40 bg-white/5'
                    : 'border-white/20 hover:border-white/30 hover:bg-white/5'
                } ${isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef.current?.click()
                }}
              >
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
                    {isUploading ? (
                      <div className="w-6 h-6 border-2 border-white/20 border-t-white animate-spin rounded-full" />
                    ) : (
                      <FileSpreadsheet className="w-8 h-8 text-white opacity-80" />
                    )}
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-white mb-2">
                      {isUploading ? t('processing_file') : t('drop_file')}
                    </h3>
                    <p className="text-white opacity-60 text-sm">
                      {t('supported_formats')}
                    </p>
                  </div>

                  {!isUploading && (
                    <div className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-md">
                      <Upload className="w-4 h-4 text-white opacity-80" />
                      <span className="text-white opacity-80 text-sm">
                        {t('choose_file')}
                      </span>
                    </div>
                  )}
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept=".csv,.xlsx"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  disabled={isUploading}
                />
              </div>
            ) : (
              /* Table Editor */
              <div className="space-y-4 relative flex flex-col flex-1 min-h-0">
                {isDetectingTypes && (
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center rounded-md">
                    <div className="bg-[#222222] border border-white/10 rounded-lg p-6 flex items-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white animate-spin rounded-full" />
                      <span className="text-white text-sm">
                        Detecting column types...
                      </span>
                    </div>
                  </div>
                )}

                {/* Type Detection Complete Message */}
                {showTypeDetectionMessage && (
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center rounded-md">
                    <div className="bg-[#222222] border border-white/10 rounded-lg p-6 max-w-md mx-4">
                      <div className="text-center space-y-4">
                        <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto">
                          <AlertTriangle className="w-6 h-6 text-white opacity-80" />
                        </div>
                        <div>
                          <h3 className="text-white font-medium text-lg mb-2">
                            {t('column_types_detected')}
                          </h3>
                          <p className="text-white opacity-70 text-sm leading-relaxed">
                            {t('automatic_type_detection_not_always_perfect')}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setShowTypeDetectionMessage(false)
                            // Force a re-render to ensure width expansion
                            setTimeout(() => {
                              // This forces React to recalculate the layout
                            }, 10)
                          }}
                          className="w-full px-4 py-2 bg-white text-black font-medium rounded-md hover:bg-white/90 transition-colors"
                        >
                          {t('review_the_columns')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="border border-white/10 rounded-md overflow-hidden bg-[#010101] relative flex-1 flex flex-col min-h-0 max-h-[400px]">
                  <div
                    className="overflow-auto flex-1 scrollbar-thin scrollbar-track-[#1A1A1A] scrollbar-thumb-[#333333] hover:scrollbar-thumb-[#404040]"
                    ref={tableRef}
                  >
                    <table
                      className="w-full border-collapse"
                      style={{ borderSpacing: 0 }}
                    >
                      <thead className="bg-[#222222] border-b-2 border-[#333333] sticky top-0 z-40">
                        <tr className="bg-[#222222]">
                          {tableData.columns?.map((column, index) => (
                            <th
                              key={index}
                              className={`relative border-r border-[#333333] group bg-[#222222] ${
                                draggingColumn === index ? 'opacity-50' : ''
                              } ${
                                dragOverColumn === index
                                  ? 'bg-blue-500/20 border-l-2 border-l-blue-500'
                                  : ''
                              }`}
                              style={{ minWidth: `${column.width}px` }}
                              draggable
                              onDragStart={(e) =>
                                handleColumnDragStart(e, index)
                              }
                              onDragOver={(e) => handleColumnDragOver(e, index)}
                              onDragLeave={handleColumnDragLeave}
                              onDrop={(e) => handleColumnDrop(e, index)}
                              onDragEnd={handleColumnDragEnd}
                            >
                              {/* Column Header */}
                              <div className="flex items-center justify-between p-3">
                                <div className="flex items-center gap-2 flex-1">
                                  {/* Drag Handle - 6 dots */}
                                  <div
                                    className="flex items-center justify-center w-5 h-5 cursor-grab active:cursor-grabbing hover:bg-white/5 rounded transition-colors"
                                    title="Drag to reorder column"
                                  >
                                    <GripVertical className="w-3 h-3 text-white opacity-50 hover:text-white hover:opacity-80" />
                                  </div>

                                  {/* Column Type Icon */}
                                  <div className="flex items-center justify-center w-5 h-5">
                                    {(() => {
                                      const columnType = columnTypes.find(
                                        (type) => type.value === column.type,
                                      )
                                      const IconComponent =
                                        columnType?.icon || Type
                                      return (
                                        <IconComponent className="w-4 h-4 text-white opacity-60" />
                                      )
                                    })()}
                                  </div>

                                  <div className="relative flex-1">
                                    <button
                                      onClick={() =>
                                        setColumnDropdowns({
                                          ...columnDropdowns,
                                          [index]: !columnDropdowns[index],
                                        })
                                      }
                                      className="flex items-center gap-2 text-left w-full"
                                    >
                                      <span className="text-white font-medium text-sm truncate">
                                        {column.name}
                                      </span>
                                      <ChevronDown className="w-3 h-3 text-white opacity-60" />
                                    </button>

                                    {/* Column Dropdown */}
                                    {columnDropdowns[index] && (
                                      <div className="dropdown-container absolute top-full right-0 mt-1 bg-[#2B2B2B] border border-white/10 rounded-md shadow-lg z-[9999] min-w-48">
                                        <div className="p-3 space-y-3">
                                          <div>
                                            <label className="text-white opacity-60 text-xs mb-1 block">
                                              {t('column_name')}
                                            </label>
                                            <input
                                              type="text"
                                              value={column.name}
                                              onChange={(e) =>
                                                updateColumnName(
                                                  index,
                                                  e.target.value,
                                                )
                                              }
                                              className="w-full px-2 py-1 bg-[#4A4A4A] border border-white/10 rounded text-white font-light text-sm"
                                            />
                                          </div>
                                          <div>
                                            <label className="text-white opacity-60 text-xs mb-1 block">
                                              {t('type')}
                                            </label>
                                            <select
                                              value={column.type}
                                              onChange={(e) =>
                                                updateColumnType(
                                                  index,
                                                  e.target.value,
                                                )
                                              }
                                              className="w-full px-2 py-1 bg-[#4A4A4A] border border-white/10 rounded text-white font-light text-sm"
                                            >
                                              {columnTypes.map((type) => (
                                                <option
                                                  key={type.value}
                                                  value={type.value}
                                                >
                                                  {type.label}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                          <button
                                            onClick={() => deleteColumn(index)}
                                            className="w-full px-2 py-1 bg-red-600/20 hover:bg-red-600/30 border border-red-600/30 rounded text-red-400 text-sm transition-colors"
                                          >
                                            {t('delete_column')}
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Column Resize Handle */}
                              <div
                                className="absolute right-0 top-0 bottom-0 w-1 bg-white/20 hover:bg-white/40 cursor-col-resize z-10 opacity-0 hover:opacity-100 transition-opacity"
                                onMouseDown={(e) => handleResizeStart(index, e)}
                                title="Drag to resize column"
                              />
                            </th>
                          ))}

                          {/* Add Column Button */}
                          <th className="relative bg-[#222222] w-16 sticky right-0 z-50">
                            <div className="dropdown-container flex items-center justify-center p-3">
                              <button
                                ref={addColumnButtonRef}
                                onClick={handleAddColumnClick}
                                className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-md flex items-center justify-center transition-colors border border-white/10"
                                title="Add Column"
                              >
                                <Plus className="w-4 h-4 text-white opacity-80" />
                              </button>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.rows
                          .slice(0, visibleRows)
                          .map((row, rowIndex) => {
                            const hasImages = tableData.columns
                              ? rowHasImages(row, tableData.columns)
                              : false
                            return (
                              <tr
                                key={rowIndex}
                                className={`border-b border-[#333333] last:border-b-0 ${
                                  rowIndex % 2 === 0
                                    ? 'bg-[#2B2B2B]'
                                    : 'bg-[#1A1A1A]'
                                }`}
                                style={{
                                  height: hasImages
                                    ? 'calc(2.5rem * 1.1)'
                                    : 'auto',
                                }}
                              >
                                {tableData.columns?.map((column, colIndex) => (
                                  <td
                                    key={colIndex}
                                    className="border-r border-[#333333]"
                                    style={{ minWidth: `${column.width}px` }}
                                  >
                                    {renderCellContent(
                                      column,
                                      row[colIndex] || '',
                                      rowIndex,
                                      colIndex,
                                    )}
                                  </td>
                                ))}

                                {/* Delete Row Button - aligned with Add Column button */}
                                <td
                                  className="w-16 sticky right-0 z-5"
                                  style={{
                                    backgroundColor:
                                      rowIndex % 2 === 0
                                        ? '#2B2B2B'
                                        : '#1A1A1A',
                                  }}
                                >
                                  <div className="flex items-center justify-center h-full p-2">
                                    <button
                                      onClick={() => deleteRow(rowIndex)}
                                      className="w-6 h-6 flex items-center justify-center hover:bg-red-600/30 rounded transition-colors group"
                                      title="Delete Row"
                                    >
                                      <Trash2 className="w-3 h-3 text-red-400/60 group-hover:text-red-400" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                      </tbody>
                    </table>
                  </div>

                  {/* Table Footer */}
                  <div className="flex items-center justify-between px-4 py-3 bg-[#222222] border-t border-white/10 flex-shrink-0">
                    <button
                      onClick={addRow}
                      className="flex items-center gap-2 px-3 py-1 hover:bg-white/10 rounded transition-colors text-white opacity-80 hover:text-white text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      {t('add_row')}
                    </button>

                    <div className="relative">
                      <button
                        onClick={() => setShowRowsDropdown(!showRowsDropdown)}
                        className={`flex items-center gap-1 text-xs transition-colors ${
                          visibleRows >= tableData.rows.length
                            ? 'text-white opacity-40 cursor-not-allowed'
                            : 'text-white opacity-60 hover:text-white cursor-pointer'
                        }`}
                        disabled={visibleRows >= tableData.rows.length}
                      >
                        {visibleRows >= tableData.rows.length
                          ? t('all_rows_shown', {
                              count: tableData.rows.length,
                            })
                          : t('showing_rows_of_rows', {
                              count: Math.min(
                                visibleRows,
                                tableData.rows.length,
                              ),
                              total: tableData.rows.length,
                            })}
                        {visibleRows < tableData.rows.length && (
                          <ChevronDown className="w-3 h-3" />
                        )}
                      </button>

                      {showRowsDropdown &&
                        visibleRows < tableData.rows.length && (
                          <div className="dropdown-container absolute bottom-full right-0 mb-1 bg-[#2B2B2B] border border-white/10 rounded-md shadow-lg z-[9999] min-w-32">
                            <div className="p-2">
                              {visibleRows < tableData.rows.length && (
                                <button
                                  onClick={() => {
                                    setVisibleRows(visibleRows + 5)
                                    setShowRowsDropdown(false)
                                  }}
                                  className="w-full text-left px-3 py-2 hover:bg-white/10 rounded text-white text-sm transition-colors"
                                >
                                  {t('show_5_more')}
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setVisibleRows(tableData.rows.length)
                                  setShowRowsDropdown(false)
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-white/10 rounded text-white text-sm transition-colors"
                              >
                                {t('show_all')}
                              </button>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {tableData && (
            <div className="flex items-center justify-end gap-3 p-6 border-t border-white/10">
              <button
                onClick={onClose}
                className="px-4 py-2 text-white opacity-80 hover:text-white transition-colors hover:bg-white/10 rounded-md"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSaveTable}
                disabled={isSaving}
                className="px-6 py-2 bg-white text-black font-medium rounded-md hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving && (
                  <div className="w-4 h-4 border-2 border-black/20 border-t-black animate-spin rounded-full" />
                )}
                {isSaving ? t('saving') : t('save_table')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Floating Add Column Dropdown */}
      {addColumnDropdown && addColumnButtonPosition && (
        <div
          className="dropdown-container fixed z-[100005] bg-[#2B2B2B] border border-white/10 rounded-md shadow-lg min-w-40"
          style={{
            left: addColumnButtonPosition.x,
            top: addColumnButtonPosition.y,
          }}
        >
          <div className="p-1">
            {columnTypes.map((type) => {
              const IconComponent = type.icon
              return (
                <button
                  key={type.value}
                  onClick={() => addColumn(type.value)}
                  className="w-full text-left px-3 py-1.5 hover:bg-white/10 rounded text-white text-sm transition-colors flex items-center gap-2"
                >
                  <IconComponent className="w-4 h-4 text-white opacity-60" />
                  <span className="font-normal">{type.label}</span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {imagePreview && (
        <>
          <div
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100001]"
            onClick={() => setImagePreview(null)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-[100002] p-4">
            <div className="relative max-w-4xl max-h-[90vh] bg-white/5 backdrop-blur-lg rounded-lg overflow-hidden">
              <button
                onClick={() => setImagePreview(null)}
                className="absolute top-2 right-2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center transition-colors z-10"
              >
                <X className="w-4 h-4 text-white" />
              </button>
              <img
                src={imagePreview.url}
                alt={imagePreview.alt}
                className="max-w-full max-h-[90vh] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 backdrop-blur-sm p-3">
                <p className="text-white text-sm truncate">
                  {imagePreview.alt}
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100003]"
            onClick={() => setShowConfirmModal(false)}
          />
          <div className="fixed inset-0 flex items-center justify-center z-[100004] p-4">
            <div className="bg-gradient-to-br from-[#222222]/95 via-[#2B2B2B] to-[#010101]/95 backdrop-blur-lg rounded-xl shadow-2xl border border-white/10 max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-white">
                    {t('confirm_save')}
                  </h3>
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="text-white opacity-60 hover:text-white transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-white opacity-80 text-sm mb-6 text-left">
                  {t(
                    'you_wont_be_able_to_edit_this_table_again_until_your_project_is_created',
                  )}
                </p>
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => setShowConfirmModal(false)}
                    className="px-4 py-2 text-white opacity-80 hover:text-white transition-colors hover:bg-white/10 rounded-md"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    onClick={handleConfirmSave}
                    disabled={isSaving}
                    className="px-6 py-2 bg-white text-black font-medium rounded-md hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSaving && (
                      <div className="w-4 h-4 border-2 border-black/20 border-t-black animate-spin rounded-full" />
                    )}
                    {isSaving ? t('saving') : t('save_table')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  )
}
